import {
  Component, HostListener, computed, inject, signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import {
  ampli,
  HELP_HUB_CONTACT_SUBJECTS,
  HELP_HUB_SECTIONS,
  HelpHubItem,
  HelpHubSection,
  LanguageService,
  SupportService,
} from '@instaclause-webapp/shared-core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { DropdownOption, FieldState } from '../../models/field.model';
import { InlineSvgComponent } from '../inline-svg/inline-svg.component';
import { TooltipDirective } from '../tooltip/tooltip.directive';


type HelpHubView = 'menu' | 'contact';

const ATTACH_SUPPORTED_EXTENSIONS = [
  '.png', '.jpg', '.jpeg', '.gif', '.pdf', '.doc', '.docx', '.txt',
];
const ATTACH_ACCEPT = ATTACH_SUPPORTED_EXTENSIONS.join(',');

@Component({
  selector: 'ui-help-hub',
  imports: [
    TranslocoModule,
    InlineSvgComponent,
    TooltipDirective,
  ],
  providers: [ SupportService ],
  templateUrl: './help-hub.component.html',
  styleUrl: './help-hub.component.scss',
})
export class HelpHubComponent {
  isOpen = signal<boolean>(false);
  view = signal<HelpHubView>('menu');
  sections: HelpHubSection[] = inject(HELP_HUB_SECTIONS);

  contactSubject = signal<string | undefined>(undefined);
  contactMessage = signal<string>('');
  attachedFile = signal<File | null>(null);
  attachError = signal<string | null>(null);
  acceptedFileTypes = ATTACH_ACCEPT;

  submitAttempted = signal<boolean>(false);
  submitting = signal<boolean>(false);
  submitted = signal<boolean>(false);
  submitFailed = signal<boolean>(false);

  subjectOptions = computed<DropdownOption[]>(() => {
    const lang = this.currentLanguage();

    return HELP_HUB_CONTACT_SUBJECTS.map((id) => ({
      value: id,
      label: this.translocoService.translate(
        `HELP_HUB.CONTACT.SUBJECTS.${id.toUpperCase()}`,
        undefined,
        lang,
      ),
    }));
  });

  subjectInvalid = computed(() => this.submitAttempted() && !this.contactSubject());
  messageInvalid = computed(() => this.submitAttempted() && !this.contactMessage().trim());
  messageState = computed(() => (this.messageInvalid() ? FieldState.Invalid : FieldState.Default));

  private router = inject(Router);
  private translocoService = inject(TranslocoService);
  private supportService = inject(SupportService);
  private currentLanguage = toSignal(inject(LanguageService).language$);

  get contactEnabled(): boolean {
    return this.supportService.enabled;
  }

  @HostListener('document:click', [ '$event' ])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen()) {
      return;
    }

    const clickedInside = (event.target as HTMLElement).closest('.help-hub');

    if (!clickedInside) {
      this.closeHub();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) {
      this.closeHub();
    }
  }

  toggle(): void {
    if (this.isOpen()) {
      this.closeHub();

      return;
    }

    this.isOpen.set(true);
    ampli.helphubOpened({ source_page: this.router.url });
  }

  onItemClick(item: HelpHubItem): void {
    ampli.helphubItemClicked({ item: item.id, source_page: this.router.url });

    if (item.action === 'internal') {
      this.openContactForm();

      return;
    }

    this.closeHub();
  }

  resolveItemUrl(item: HelpHubItem): string | undefined {
    const lang = this.currentLanguage();

    return (lang && item.localizedUrls?.[lang]) || item.url;
  }

  backToMenu(): void {
    this.resetContactForm();
    this.view.set('menu');
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (!file) {
      this.attachedFile.set(null);

      return;
    }

    const lowerName = file.name.toLowerCase();
    const supported = ATTACH_SUPPORTED_EXTENSIONS.some((ext) => lowerName.endsWith(ext));

    if (!supported) {
      this.attachedFile.set(null);
      this.attachError.set(this.translocoService.translate('HELP_HUB.CONTACT.ATTACH_UNSUPPORTED'));

      return;
    }

    this.attachedFile.set(file);
    this.attachError.set(null);
  }

  removeFile(): void {
    this.attachedFile.set(null);
    this.attachError.set(null);
  }

  async onSubmitContact(): Promise<void> {
    if (!this.contactEnabled) {
      return;
    }

    this.submitAttempted.set(true);

    if (this.subjectInvalid() || this.messageInvalid()) {
      return;
    }

    const subject = this.contactSubject()!;
    this.submitting.set(true);
    this.submitFailed.set(false);

    try {
      await firstValueFrom(
        this.supportService.submitContactRequest({
          subject,
          message: this.contactMessage().trim(),
          file: this.attachedFile(),
        }),
      );

      ampli.helphubContactSubmitted({ subject });
      this.submitted.set(true);
    } catch {
      this.submitFailed.set(true);
    } finally {
      this.submitting.set(false);
    }
  }

  private openContactForm(): void {
    this.resetContactForm();
    this.view.set('contact');
  }

  private closeHub(): void {
    this.isOpen.set(false);
    this.resetContactForm();
    this.view.set('menu');
  }

  private resetContactForm(): void {
    this.contactSubject.set(undefined);
    this.contactMessage.set('');
    this.attachedFile.set(null);
    this.attachError.set(null);
    this.submitAttempted.set(false);
    this.submitting.set(false);
    this.submitted.set(false);
    this.submitFailed.set(false);
  }
}
